import axios from "axios";
import { appContextFromLocation, STORE_HOSTS } from "@/lib/appContext";

// Always call the API on the SAME origin the page was loaded from (the ingress routes /api on every host):
// first-party cookies (iOS Safari / installed PWA block third-party cookies), no CORS, and every domain
// (store apex/www, *.emergent.host, preview) works regardless of the configured allow-list.
// Only plain local development (localhost) falls back to REACT_APP_BACKEND_URL.
export { STORE_HOSTS };
const sameOrigin = typeof window !== "undefined" && !["localhost", "127.0.0.1"].includes(window.location.hostname);
export const BACKEND_URL = sameOrigin ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export function appApiUrl(path) {
  const value = String(path || "");
  const delimiter = value.includes("?") ? "&" : "?";
  return `${API_BASE}${value}${delimiter}app=${encodeURIComponent(appContextFromLocation())}`;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

// ---- store customer session fallback ---------------------------------------
// The public store lives on its own domain while the API is on another host; when the browser blocks
// third-party cookies the customer JWT is sent as a header instead (backend accepts both).
export const CUSTOMER_TOKEN_KEY = "sl_customer_token";
let customerTokenAccess = false;
export function setCustomerTokenAccess(enabled) {
  customerTokenAccess = Boolean(enabled);
}
api.interceptors.request.use((config) => {
  config.headers["X-OWLICO-App"] = appContextFromLocation();
  if (customerTokenAccess && (config.url || "").startsWith("/store")) {
    const tok = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (tok) config.headers["X-Customer-Token"] = tok;
  }
  return config;
});

// ---- silent session renewal -------------------------------------------------
// On a 401 we try ONE refresh (deduplicated across concurrent requests) and replay the call.
// Only when the refresh itself fails do we tell the app the session is really over.
let refreshing = null;
const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout"];

export function refreshSession() {
  if (!refreshing) {
    refreshing = axios
      .post(`${API_BASE}/auth/refresh`, null, {
        withCredentials: true,
        headers: { "X-OWLICO-App": appContextFromLocation() },
      })
      .then((r) => r.data)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const url = config?.url || "";
    if (response?.status !== 401 || !config || config.__retried || url.startsWith("/store") || NO_REFRESH.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    try {
      await refreshSession();
    } catch (refreshError) {
      window.dispatchEvent(new CustomEvent("owlico:session-expired"));
      return Promise.reject(error);
    }
    config.__retried = true;
    return api(config);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (detail?.code === "publication_incomplete" && Array.isArray(detail.missing)) {
    return `Publication incomplete: ${detail.missing.join(", ")}.`;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail === "object") return "Request could not be completed.";
  return String(detail);
}
